/**
 * Keeps the Realtime socket's access token fresh.
 *
 * Realtime only learns about a new token when something calls `setAuth()`, and
 * supabase-js does not schedule that itself when the client is built with the
 * `accessToken` callback option (it skips its own auth-event listener in that
 * mode). What refresh does happen rides along with the 25s heartbeat, which
 * works out to roughly every 50s.
 *
 * Clerk session tokens live 60 seconds and `getToken()` serves a cached one
 * until it is nearly expired, so a token pushed on that cadence can already be
 * most of the way through its life. When it expires before the next push lands,
 * the Realtime server closes every channel — and a closed channel removes
 * itself from the socket, so nothing rejoins it.
 *
 * So rather than a fixed interval, schedule off the token's own `exp`: refresh
 * a comfortable margin before it dies, which keeps the token the server holds
 * from ever reaching expiry.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

/** Refresh this long before the current token expires. */
const REFRESH_MARGIN_MS = 20_000;
/** Used when the token is missing or its expiry cannot be read. */
const FALLBACK_INTERVAL_MS = 30_000;
/** Never schedule tighter than this, so a bad `exp` cannot spin the timer. */
const MIN_INTERVAL_MS = 5_000;

interface RealtimeAuthTarget {
  accessTokenValue?: string | null;
  setAuth: (token?: string | null) => Promise<void>;
}

function getRealtime(client: SupabaseClient<any>): RealtimeAuthTarget | null {
  const rt = (client as unknown as { realtime?: RealtimeAuthTarget }).realtime;
  return rt && typeof rt.setAuth === 'function' ? rt : null;
}

/** Milliseconds until the JWT's `exp`, or null if it cannot be read. */
function msUntilExpiry(token: string | null | undefined): number | null {
  if (!token) return null;
  const claims = token.split('.')[1];
  if (!claims) return null;
  try {
    const payload = JSON.parse(atob(claims.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000 - Date.now();
  } catch {
    return null;
  }
}

/**
 * Begin refreshing `client`'s realtime token. Returns a stop function.
 *
 * Safe to call again for a new client (see reinitializeSupabase) — each call
 * drives only the client it was given.
 */
export function startRealtimeTokenRefresh(client: SupabaseClient<any>): () => void {
  if (typeof window === 'undefined') return () => {};

  const realtime = getRealtime(client);
  if (!realtime) return () => {};

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const scheduleNext = () => {
    if (stopped) return;
    const remaining = msUntilExpiry(realtime.accessTokenValue);
    const delay =
      remaining === null
        ? FALLBACK_INTERVAL_MS
        : Math.max(remaining - REFRESH_MARGIN_MS, MIN_INTERVAL_MS);
    timer = setTimeout(refresh, delay);
  };

  const refresh = () => {
    timer = null;
    if (stopped) return;
    // setAuth() with no argument re-reads the accessToken callback (Clerk), and
    // pushes to every joined channel only when the token actually changed.
    realtime
      .setAuth()
      .catch((err) => {
        console.debug('[realtimeAuth] token refresh failed:', err);
      })
      .finally(scheduleNext);
  };

  scheduleNext();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    timer = null;
  };
}
